import React from "react";
import { CheckCircle2, Users, ClipboardCheck, HeartHandshake, BookOpen, TrendingUp, ShieldCheck, GraduationCap } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { Container, SectionHeader, Eyebrow } from "@/components/Section";
import { useSite } from "@/context/SiteContext";
import { resolveImg } from "@/lib/api";

const FOCUS = [
  { icon: HeartHandshake, title: "Personal Attention", desc: "Every student is known by name and mentored individually." },
  { icon: Users, title: "Experienced Teaching", desc: "Seasoned faculty who make difficult concepts simple." },
  { icon: BookOpen, title: "Small Batches", desc: "Limited seats so no student is ever left behind." },
  { icon: ClipboardCheck, title: "Regular Testing", desc: "Continuous assessment keeps learning on track." },
  { icon: TrendingUp, title: "Student Progress", desc: "Clear, measurable growth tracked through the year." },
  { icon: ShieldCheck, title: "Parent Communication", desc: "Frequent updates so families stay involved." },
];

const STORY_ONE = [
  "Prasad Tutor Group began with just one student — Alpa Chadva.",
  "At the time, Krunal was unsure about taking up a conventional job. His father encouraged him to consider teaching and build something of his own. What started as home tuition for one student soon grew through word of mouth. A few more students joined, and with every student, Krunal’s interest in teaching became stronger.",
  "A major turning point came when a tuition class in the neighbourhood closed. Around 17 of their students joined Krunal, who was already teaching approximately 18 students. His small group quickly grew to around 35 students.",
  "As the number of students increased, Krunal rented his first classroom. Some of his senior students were given opportunities to help teach younger learners, and gradually a small teaching team began to take shape.",
  "Krunal then focused strongly on Standards 9 and 10, particularly supporting students who lacked confidence or were struggling academically. His approach was to understand each student’s difficulties, strengthen the basics, practise consistently and provide personal attention. At the time, he became known locally for his bold “pass or fees back” commitment.",
  "The name “Prasad Sir” also became familiar among students and parents. “Prasad” was the name associated with the family home, and over time that identity grew into Prasad Tutor Group — PTG.",
  "Krunal’s father remained an important source of support during the early journey. After his father passed away, teaching and his students became an even bigger part of Krunal’s life. He spent his days surrounded by students, teaching and building a learning community that went far beyond a conventional tuition class.",
];

const STORY_TWO = [
  "Pooja joined Krunal in the journey of PTG and became an important part of building the group together.",
  "As PTG continued to grow, Pooja took responsibility for sections including Standards 9 and 10, while Krunal expanded his focus to the college section. Another classroom was added, more teachers joined, and PTG continued to grow through student and parent recommendations.",
  "At its peak, PTG was teaching approximately 350–400 students in a year.",
  "But growth was never only about numbers.",
  "From students who struggled with the basics to high-achieving students aiming for stronger percentages, the philosophy remained the same: meet every learner at their level and help them move forward.",
];

const TEACHERS = [
  { name: "Mittal Miss", role: "Pre-Primary Educator", desc: "Specialises in teaching young learners with patience, care and engaging learning activities, helping children build strong early learning foundations." },
  { name: "Meghna Miss", role: "CBSE & ICSE Educator", desc: "Experienced in supporting CBSE and ICSE students with clear concept-based teaching, regular practice and focused academic guidance." },
  { name: "Komal Miss", role: "Standard 10 & Mathematics Educator", desc: "Specialises in Standard 10 and Mathematics, with a strong focus on concept clarity, exam preparation, regular practice and building students’ confidence." },
  { name: "Sandhya Miss", role: "Standard 9 & Mathematics Educator", desc: "Known for focused teaching in Standard 9 and Mathematics, helping students strengthen their fundamentals, overcome learning gaps and make consistent academic progress." },
  { name: "Riya Miss", role: "Standards 5–7 Educator", desc: "Supports middle-school learners with clear explanations, regular practice and personal attention, helping students develop stronger concepts and confidence." },
];

export default function About() {
  const { site } = useSite();
  return (
    <div data-testid="about-page">
      <Seo title="About Us" description="Learn about Prasad Tutor Group (PTG), Khar East, Mumbai — providing education and coaching since 2003 with personal attention and small batches." />

      <section className="bg-blue-950 py-16 sm:py-20 text-white">
        <Container>
          <Eyebrow>About PTG</Eyebrow>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight max-w-3xl">Building strong academic foundations since 2003</h1>
          <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">
            Prasad Tutor Group (PTG) has been providing quality education and coaching since 2003, earning the trust of families through consistency, care and results.
          </p>
        </Container>
      </section>

      {/* FOUNDER STORY */}
      <section className="py-16 sm:py-24" data-testid="founder-section">
        <Container>
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start">
            <Reveal className="lg:col-span-2">
              <div className="lg:sticky lg:top-24">
                {site.founder_photo ? (
                  <img src={resolveImg(site.founder_photo)}
                    alt="Krunal and Pooja, founders of Prasad Tutor Group (PTG), Khar East, Mumbai"
                    className="w-full rounded-3xl shadow-2xl object-cover object-top aspect-[4/5]"
                    data-testid="founder-photo" />
                ) : (
                  <div className="w-full aspect-[4/5] rounded-3xl border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-slate-400">
                    <Users className="h-12 w-12" />
                  </div>
                )}
                <p className="mt-3 text-center text-sm font-semibold text-slate-500">Krunal &amp; Pooja Parmar · Founders of PTG</p>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="lg:col-span-3">
              <Eyebrow>Our Story</Eyebrow>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" data-testid="founder-heading">
                From One Student to the PTG Family
              </h2>
              <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">
                {STORY_ONE.map((para, i) => <p key={i}>{para}</p>)}
              </div>

              <h3 className="mt-10 font-heading text-2xl font-extrabold text-blue-900 tracking-tight" data-testid="founder-subheading">
                Krunal &amp; Pooja — Growing PTG Together
              </h3>
              <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">
                {STORY_TWO.map((para, i) => <p key={i}>{para}</p>)}
              </div>

              <div className="mt-8 rounded-2xl bg-blue-900 text-white px-6 py-5 text-center shadow-lg">
                <p className="font-heading text-lg sm:text-xl font-extrabold tracking-tight">
                  Personal Attention. Stronger Concepts. Regular Practice. Real Progress.
                </p>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* OUR FOCUS */}
      <section className="py-16 sm:py-24 bg-slate-50 ptg-grid-bg">
        <Container>
          <SectionHeader eyebrow="Our Focus" title="What we care about most" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FOCUS.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.06}>
                <div className="h-full bg-white rounded-2xl border border-slate-200 shadow-sm p-7 hover:shadow-lg transition-all" data-testid={`focus-${i}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-blue-900"><f.icon className="h-6 w-6" /></div>
                  <h3 className="mt-4 text-lg font-bold text-slate-800">{f.title}</h3>
                  <p className="mt-1.5 text-slate-600 text-sm leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* OUR TEACHERS */}
      <section className="py-16 sm:py-24" data-testid="teachers-section">
        <Container>
          <SectionHeader eyebrow="Our Teachers" title="Meet the PTG faculty"
            subtitle="A dedicated team focused on concept clarity, regular practice and personal attention." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TEACHERS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.06}>
                <div className="h-full bg-white rounded-2xl border border-slate-200 shadow-sm p-6 hover:shadow-lg transition-all flex flex-col" data-testid={`teacher-card-${i}`}>
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-900 to-sky-600 text-white font-heading font-extrabold text-xl">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 leading-tight" data-testid={`teacher-name-${i}`}>{t.name}</h3>
                      <p className="text-sm font-semibold text-blue-700 flex items-center gap-1.5"><GraduationCap className="h-4 w-4" /> {t.role}</p>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-slate-600 leading-relaxed">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* WHY PARENTS CHOOSE PTG */}
      <section className="py-16 sm:py-24 bg-slate-50 ptg-grid-bg" data-testid="parent-trust-section">
        <Container>
          <SectionHeader eyebrow="Parent Trust" title="Why Parents Choose PTG"
            subtitle="Built on personal attention, consistent guidance and a commitment to helping every learner progress." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: HeartHandshake, title: "Personal Attention", desc: "Small, focused learning environments help teachers understand individual student needs." },
              { icon: BookOpen, title: "Stronger Concepts", desc: "We focus on building clear fundamentals before moving students towards more challenging work." },
              { icon: ClipboardCheck, title: "Regular Practice", desc: "Consistent practice and guidance help students build confidence and improve step by step." },
              { icon: Users, title: "Support for Every Learner", desc: "From students who need additional support to high-achieving learners aiming higher, PTG works to help each student progress." },
            ].map((t, i) => (
              <Reveal key={t.title} delay={i * 0.06}>
                <div className="h-full bg-white rounded-2xl border border-slate-200 shadow-sm p-7 hover:shadow-lg transition-all" data-testid={`trust-card-${i}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-900 text-white"><t.icon className="h-6 w-6" /></div>
                  <h3 className="mt-4 text-base font-extrabold uppercase tracking-wide text-slate-900">{t.title}</h3>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
    </div>
  );
}
